import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';

// Scroll-Position Storage Keys
const SCROLL_STORAGE_PREFIX = 'pageScrollPosition_';
const RESTAURANT_LIST_SCROLL_PREFIX = 'restaurantListScroll_';
const RESTAURANT_LIST_PATH = '/restaurants';

// Throttle utility for scroll events
function throttle<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastExecTime = 0;
  return ((...args: any[]) => {
    const currentTime = Date.now();
    if (currentTime - lastExecTime > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  }) as T;
}

export function useScrollRestoration() {
  const [location] = useLocation();
  const isInitialMount = useRef(true);
  const navigationTypeRef = useRef<'push' | 'pop' | 'replace'>('push');
  const lastScrollPositionRef = useRef(0);

  // Browser Scroll-Restoration auf manual setzen
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  // POP vs PUSH erkennen (verbesserte Implementierung)
  useEffect(() => {
    const handlePopState = () => {
      navigationTypeRef.current = 'pop';
    };

    window.addEventListener('popstate', handlePopState);

    // Navigation Type tracking mit Wouter
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(state, title, url) {
      navigationTypeRef.current = 'push';
      return originalPushState.call(this, state, title, url);
    };

    history.replaceState = function(state, title, url) {
      navigationTypeRef.current = 'replace';
      return originalReplaceState.call(this, state, title, url);
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Scroll-Position speichern (throttled + cleanup)
  const saveScrollPosition = useCallback(throttle(() => {
    const currentScrollY = window.scrollY;
    if (Math.abs(currentScrollY - lastScrollPositionRef.current) < 10) return; // Ignore small changes

    lastScrollPositionRef.current = currentScrollY;

    if (location.startsWith(RESTAURANT_LIST_PATH)) {
      // Für Restaurant-Liste: inkl. Query-String für Filter-Zustand
      const storageKey = `${RESTAURANT_LIST_SCROLL_PREFIX}${location}`;
      sessionStorage.setItem(storageKey, currentScrollY.toString());
    } else {
      // Für andere Seiten
      const storageKey = `${SCROLL_STORAGE_PREFIX}${location}`;
      sessionStorage.setItem(storageKey, currentScrollY.toString());
    }
  }, 100), [location]);

  // Scroll-Verhalten basierend auf Navigation Type (useLayoutEffect für vor-Paint)
  useLayoutEffect(() => {
    const currentPath = location;
    const navigationType = navigationTypeRef.current;

    // Bei PUSH/REPLACE Navigation: immer nach oben scrollen (vor Paint)
    if (navigationType === 'push' || navigationType === 'replace') {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      // Reset navigation type NACH dem Scroll (nicht vorher!)
      setTimeout(() => {
        navigationTypeRef.current = 'push';
      }, 0);
      return;
    }

    // Bei POP Navigation: Scroll-Position restaurieren
    if (navigationType === 'pop') {
      let savedPosition = 0;

      if (currentPath.startsWith(RESTAURANT_LIST_PATH)) {
        // Spezielle Behandlung für Restaurant-Liste mit Query-String
        const storageKey = `${RESTAURANT_LIST_SCROLL_PREFIX}${currentPath}`;
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          savedPosition = parseInt(saved, 10);
        }
      } else {
        // Für andere Seiten
        const storageKey = `${SCROLL_STORAGE_PREFIX}${currentPath}`;
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          savedPosition = parseInt(saved, 10);
        }
      }

      // Sofort restaurieren (useLayoutEffect läuft vor Paint)
      if (savedPosition > 0) {
        window.scrollTo({ top: savedPosition, left: 0, behavior: 'auto' });
      }
    }

    // Scroll-Event Listener für alle Seiten (throttled)
    window.addEventListener('scroll', saveScrollPosition, { passive: true });

    // Cleanup: Position speichern + Listener entfernen
    return () => {
      // Eine letzte Speicherung beim Verlassen der Route
      saveScrollPosition();
      window.removeEventListener('scroll', saveScrollPosition);
      // Navigation type wird beim nächsten Navigation-Event gesetzt
    };
  }, [location, saveScrollPosition]);

  // Initial Mount Handling (scrollRestoration ist manual, also nichts tun)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Bei initial load: Browser hat scrollRestoration=manual, also keine automatische Restoration
    }
  }, []);
}

